'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Wallet, Users, History, CreditCard, Plus, LogOut, Bell, Settings, Menu, X, Zap, TrendingUp, CheckCircle2, Clock, AlertCircle, FileText, Download, MoreHorizontal, Search, ChevronRight, Sparkles, Shield, RefreshCw, Package,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ───────────────────────────────────────────────────────────────────

interface ClientApplication {
  id: string;
  clientName: string;
  clientEmail: string;
  country: string;
  flag: string;
  visaType: string;
  status: 'completed' | 'processing' | 'draft' | 'rejected';
  completion: number;
  tokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

interface UsageRecord {
  id: string;
  date: string;
  clientName: string;
  action: string;
  country: string;
  flag: string;
  tokensUsed: number;
  type: 'setup' | 'full_package';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const mockClients: ClientApplication[] = [
  { id: 'ca-001', clientName: 'Ahmed Al-Rashidi', clientEmail: 'ahmed@example.com', country: 'Germany', flag: '🇩🇪', visaType: 'Schengen Tourist', status: 'completed', completion: 100, tokensUsed: 80, createdAt: '2026-04-10', updatedAt: '2026-04-18' },
  { id: 'ca-002', clientName: 'Priya Sharma', clientEmail: 'priya@example.com', country: 'Canada', flag: '🇨🇦', visaType: 'Student Visa', status: 'processing', completion: 68, tokensUsed: 40, createdAt: '2026-04-20', updatedAt: '2026-04-25' },
  { id: 'ca-003', clientName: 'Mohammed Khalid', clientEmail: 'mkhalid@example.com', country: 'United Kingdom', flag: '🇬🇧', visaType: 'Business Visa', status: 'draft', completion: 30, tokensUsed: 20, createdAt: '2026-04-24', updatedAt: '2026-04-24' },
  { id: 'ca-004', clientName: 'Sara Kowalski', clientEmail: 'sara@example.com', country: 'USA', flag: '🇺🇸', visaType: 'B1/B2 Visitor', status: 'completed', completion: 100, tokensUsed: 80, createdAt: '2026-04-05', updatedAt: '2026-04-14' },
  { id: 'ca-005', clientName: 'Fatima Al-Zahra', clientEmail: 'fatima@example.com', country: 'France', flag: '🇫🇷', visaType: 'Schengen Student', status: 'processing', completion: 55, tokensUsed: 40, createdAt: '2026-04-22', updatedAt: '2026-04-26' },
  { id: 'ca-006', clientName: 'Ravi Patel', clientEmail: 'ravi@example.com', country: 'Australia', flag: '🇦🇺', visaType: 'Work Visa', status: 'rejected', completion: 100, tokensUsed: 80, createdAt: '2026-04-01', updatedAt: '2026-04-12' },
];

const mockUsage: UsageRecord[] = [
  { id: 'u-001', date: '2026-04-26', clientName: 'Priya Sharma', action: 'Full Package Generated', country: 'Canada', flag: '🇨🇦', tokensUsed: 80, type: 'full_package' },
  { id: 'u-002', date: '2026-04-25', clientName: 'Fatima Al-Zahra', action: 'Visa Setup Created', country: 'France', flag: '🇫🇷', tokensUsed: 20, type: 'setup' },
  { id: 'u-003', date: '2026-04-24', clientName: 'Mohammed Khalid', action: 'Visa Setup Created', country: 'United Kingdom', flag: '🇬🇧', tokensUsed: 20, type: 'setup' },
  { id: 'u-004', date: '2026-04-18', clientName: 'Ahmed Al-Rashidi', action: 'Full Package Generated', country: 'Germany', flag: '🇩🇪', tokensUsed: 80, type: 'full_package' },
  { id: 'u-005', date: '2026-04-14', clientName: 'Sara Kowalski', action: 'Full Package Generated', country: 'USA', flag: '🇺🇸', tokensUsed: 80, type: 'full_package' },
  { id: 'u-006', date: '2026-04-12', clientName: 'Ravi Patel', action: 'Full Package Generated', country: 'Australia', flag: '🇦🇺', tokensUsed: 80, type: 'full_package' },
  { id: 'u-007', date: '2026-04-10', clientName: 'Ahmed Al-Rashidi', action: 'Visa Setup Created', country: 'Germany', flag: '🇩🇪', tokensUsed: 20, type: 'setup' },
  { id: 'u-008', date: '2026-04-05', clientName: 'Sara Kowalski', action: 'Visa Setup Created', country: 'USA', flag: '🇺🇸', tokensUsed: 20, type: 'setup' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusConfig = {
  completed: { label: 'Completed', icon: CheckCircle2, className: 'badge-green', bar: 'bg-green-500' },
  processing: { label: 'Processing', icon: Clock, className: 'badge-blue', bar: 'bg-blue-500' },
  draft: { label: 'Draft', icon: AlertCircle, className: 'badge-amber', bar: 'bg-amber-400' },
  rejected: { label: 'Rejected', icon: X, className: 'badge-red', bar: 'bg-red-400' },
};

const navItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'wallet', label: 'Token Wallet', icon: Wallet },
  { id: 'clients', label: 'Client Applications', icon: Users },
  { id: 'history', label: 'Usage History', icon: History },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color }: { icon: React.ElementType; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="card p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xs font-medium text-gray-400 mb-0.5">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ onTabChange }: { onTabChange: (tab: string) => void }) {
  const totalTokens = 200;
  const usedTokens = 340;
  const remainingTokens = Math.max(0, totalTokens - (usedTokens % totalTokens));
  const tokenPct = Math.round((remainingTokens / totalTokens) * 100);

  return (
    <div className="space-y-7">
      {/* Welcome banner */}
      <div className="rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-sky-600 p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-blue-100 text-sm font-medium mb-1">Agency Portal · Basic Plan</p>
            <h2 className="text-2xl font-bold">Welcome back, TravelPro Agency 👋</h2>
            <p className="text-blue-100 text-sm mt-1">You have <span className="font-bold text-white">{remainingTokens} tokens</span> remaining this cycle.</p>
          </div>
          <button
            onClick={() => onTabChange('wallet')}
            className="flex-shrink-0 flex items-center gap-2 bg-white/20 hover:bg-white/30 border border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-150"
          >
            <Zap size={15} />
            Top Up Tokens
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Tokens Remaining" value={`${remainingTokens}`} sub={`of ${totalTokens} this cycle`} color="bg-blue-500" />
        <StatCard icon={Users} label="Active Clients" value="6" sub="3 in progress" color="bg-violet-500" />
        <StatCard icon={CheckCircle2} label="Completed" value="2" sub="this month" color="bg-green-500" />
        <StatCard icon={TrendingUp} label="Tokens Used" value={`${usedTokens}`} sub="all time" color="bg-amber-500" />
      </div>

      {/* Token usage bar */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">Token Usage This Cycle</p>
            <p className="text-xs text-gray-400 mt-0.5">Resets May 1, 2026</p>
          </div>
          <span className="text-sm font-bold text-blue-600">{tokenPct}% remaining</span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-sky-400 rounded-full transition-all duration-700"
            style={{ width: `${tokenPct}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>{remainingTokens} remaining</span>
          <span>{totalTokens - remainingTokens} used</span>
        </div>
      </div>

      {/* Recent clients */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-900">Recent Client Applications</p>
          <button onClick={() => onTabChange('clients')} className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
            View all <ChevronRight size={13} />
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {mockClients.slice(0, 4).map((c) => {
            const sc = statusConfig[c.status];
            const StatusIcon = sc.icon;
            return (
              <div key={c.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/60 transition-colors">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-sky-100 flex items-center justify-center text-sm font-bold text-blue-700 flex-shrink-0">
                  {c.clientName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{c.clientName}</p>
                  <p className="text-xs text-gray-400 truncate">{c.flag} {c.country} · {c.visaType}</p>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${sc.bar} rounded-full`} style={{ width: `${c.completion}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right">{c.completion}%</span>
                </div>
                <span className={`${sc.className} hidden sm:inline-flex`}>
                  <StatusIcon size={11} />
                  {sc.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Token Wallet Tab ─────────────────────────────────────────────────────────

function WalletTab() {
  const [showTopUp, setShowTopUp] = useState(false);
  const [selectedPack, setSelectedPack] = useState<string | null>(null);

  const totalTokens = 200;
  const usedTokens = 160;
  const remainingTokens = totalTokens - usedTokens;
  const tokenPct = Math.round((remainingTokens / totalTokens) * 100);

  const topUpPacks = [
    { id: 'pack-100', tokens: 100, price: '$49', priceNote: '$0.49/token', popular: false },
    { id: 'pack-200', tokens: 200, price: '$89', priceNote: '$0.45/token', popular: true },
    { id: 'pack-500', tokens: 500, price: '$199', priceNote: '$0.40/token', popular: false },
  ];

  return (
    <div className="space-y-6">
      {/* Balance card */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-sky-500 p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4" />
        <div className="relative">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-blue-100 text-sm font-medium">Token Balance</p>
              <p className="text-5xl font-bold mt-1">{remainingTokens}</p>
              <p className="text-blue-100 text-sm mt-1">of {totalTokens} tokens this billing cycle</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <Wallet size={22} />
            </div>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${tokenPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-blue-100">
            <span>{remainingTokens} remaining</span>
            <span>{usedTokens} used · resets May 1</span>
          </div>
        </div>
      </div>

      {/* Token pricing info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <FileText size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Visa Setup</p>
              <p className="text-xs text-gray-400">Guidance + checklist</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">20 <span className="text-base font-medium text-gray-400">tokens</span></p>
          <p className="text-xs text-gray-400 mt-1">per application</p>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <Sparkles size={16} className="text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Full Package</p>
              <p className="text-xs text-gray-400">Documents + letters + PDF</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">80 <span className="text-base font-medium text-gray-400">tokens</span></p>
          <p className="text-xs text-gray-400 mt-1">per application</p>
        </div>
      </div>

      {/* Top-up section */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Top Up Tokens</p>
            <p className="text-xs text-gray-400 mt-0.5">Purchase additional tokens anytime</p>
          </div>
          <button
            onClick={() => setShowTopUp(!showTopUp)}
            className="btn-primary text-sm py-2 px-4"
          >
            <Zap size={14} />
            {showTopUp ? 'Hide' : 'Buy Tokens'}
          </button>
        </div>

        {showTopUp && (
          <div className="space-y-3 animate-fade-in">
            {topUpPacks.map((pack) => (
              <div
                key={pack.id}
                onClick={() => setSelectedPack(pack.id)}
                className={`relative flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                  selectedPack === pack.id
                    ? 'border-blue-500 bg-blue-50' :'border-gray-100 hover:border-gray-200 bg-gray-50/50'
                }`}
              >
                {pack.popular && (
                  <span className="absolute -top-2.5 left-4 badge bg-blue-600 text-white text-xs px-2 py-0.5">Most Popular</span>
                )}
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedPack === pack.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                    {selectedPack === pack.id && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{pack.tokens} Tokens</p>
                    <p className="text-xs text-gray-400">{pack.priceNote}</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-gray-900">{pack.price}</p>
              </div>
            ))}
            <button
              disabled={!selectedPack}
              className="btn-primary w-full justify-center mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CreditCard size={15} />
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>

      {/* Recent token activity */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-900">Recent Token Activity</p>
        </div>
        <div className="divide-y divide-gray-50">
          {mockUsage.slice(0, 5).map((u) => (
            <div key={u.id} className="flex items-center gap-4 px-5 py-3.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${u.type === 'full_package' ? 'bg-violet-50' : 'bg-blue-50'}`}>
                {u.type === 'full_package' ? <Sparkles size={14} className="text-violet-600" /> : <FileText size={14} className="text-blue-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{u.clientName}</p>
                <p className="text-xs text-gray-400">{u.flag} {u.action} · {u.date}</p>
              </div>
              <span className="text-sm font-bold text-red-500 flex-shrink-0">−{u.tokensUsed}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Client Applications Tab ──────────────────────────────────────────────────

function ClientsTab() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filtered = mockClients.filter((c) => {
    const matchSearch = c.clientName.toLowerCase().includes(search.toLowerCase()) ||
      c.country.toLowerCase().includes(search.toLowerCase()) ||
      c.visaType.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-5">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients, countries, visa types..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field w-auto pr-8 cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="draft">Draft</option>
            <option value="rejected">Rejected</option>
          </select>
          <Link
            href="/visa-application-wizard"
            className="btn-primary text-sm py-2 px-4 whitespace-nowrap"
          >
            <Plus size={14} />
            New Client
          </Link>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'completed', 'processing', 'draft', 'rejected'] as const).map((s) => {
          const count = s === 'all' ? mockClients.length : mockClients.filter((c) => c.status === s).length;
          return (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                filterStatus === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === 'all' ? 'All' : statusConfig[s].label} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Users size={32} className="text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-400">No clients found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Visa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Progress</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Tokens</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((c) => {
                  const sc = statusConfig[c.status];
                  const StatusIcon = sc.icon;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-sky-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                            {c.clientName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{c.clientName}</p>
                            <p className="text-xs text-gray-400">{c.clientEmail}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <p className="text-sm text-gray-700">{c.flag} {c.country}</p>
                        <p className="text-xs text-gray-400">{c.visaType}</p>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${sc.bar} rounded-full`} style={{ width: `${c.completion}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{c.completion}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={sc.className}>
                          <StatusIcon size={11} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden sm:table-cell">
                        <span className="text-sm font-semibold text-gray-700">{c.tokensUsed}</span>
                        <span className="text-xs text-gray-400 ml-1">tkn</span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <span className="text-xs text-gray-400">{c.updatedAt}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1 justify-end">
                          {c.status === 'completed' && (
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                              <Download size={14} />
                            </button>
                          )}
                          <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                            <MoreHorizontal size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Usage History Tab ────────────────────────────────────────────────────────

function HistoryTab() {
  const totalUsed = mockUsage.reduce((s, u) => s + u.tokensUsed, 0);
  const setupCount = mockUsage.filter((u) => u.type === 'setup').length;
  const fullCount = mockUsage.filter((u) => u.type === 'full_package').length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-xs font-medium text-gray-400 mb-1">Total Tokens Used</p>
          <p className="text-3xl font-bold text-gray-900">{totalUsed}</p>
          <p className="text-xs text-gray-400 mt-1">all time</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-medium text-gray-400 mb-1">Visa Setups</p>
          <p className="text-3xl font-bold text-blue-600">{setupCount}</p>
          <p className="text-xs text-gray-400 mt-1">{setupCount * 20} tokens consumed</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-medium text-gray-400 mb-1">Full Packages</p>
          <p className="text-3xl font-bold text-violet-600">{fullCount}</p>
          <p className="text-xs text-gray-400 mt-1">{fullCount * 80} tokens consumed</p>
        </div>
      </div>

      {/* Usage log */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-900">Token Usage Log</p>
          <button className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
            <Download size={13} />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Tokens</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {mockUsage.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-600">{u.date}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{u.flag}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{u.clientName}</p>
                        <p className="text-xs text-gray-400 hidden sm:block">{u.country}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <span className="text-sm text-gray-600">{u.action}</span>
                  </td>
                  <td className="px-4 py-3.5 hidden sm:table-cell">
                    <span className={`badge ${u.type === 'full_package' ? 'bg-violet-50 text-violet-700' : 'badge-blue'}`}>
                      {u.type === 'full_package' ? 'Full Package' : 'Setup'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="text-sm font-bold text-red-500">−{u.tokensUsed}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Subscription Tab ─────────────────────────────────────────────────────────

function SubscriptionTab() {
  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: '$299',
      period: '/month',
      tokens: '200 tokens/month',
      features: ['200 tokens per cycle', '20 tokens per Visa Setup', '80 tokens per Full Package', 'Client management dashboard', 'Usage history & reports', 'Email support'],
      current: true,
      cta: 'Current Plan',
      color: 'border-blue-500 bg-blue-50/30',
      badge: 'bg-blue-600 text-white',
    },
    {
      id: 'plus',
      name: 'Plus',
      price: '$999',
      period: '/month',
      tokens: 'Unlimited tokens',
      features: ['Unlimited tokens', 'Priority AI processing', 'White-label PDF exports', 'Dedicated account manager', 'API access', 'Priority support 24/7'],
      current: false,
      cta: 'Upgrade to Plus',
      color: 'border-gray-100',
      badge: 'bg-violet-600 text-white',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Current plan status */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
              <Shield size={22} className="text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-base font-bold text-gray-900">Basic Plan</p>
                <span className="badge badge-blue">Active</span>
              </div>
              <p className="text-sm text-gray-400 mt-0.5">$299/month · Renews May 1, 2026</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary text-sm py-2 px-4">
              <RefreshCw size={14} />
              Manage Billing
            </button>
            <button className="btn-secondary text-sm py-2 px-4 text-red-500 hover:text-red-600 hover:border-red-200">
              Cancel Plan
            </button>
          </div>
        </div>
      </div>

      {/* Plan comparison */}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-4">Available Plans</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {plans.map((plan) => (
            <div key={plan.id} className={`card p-6 border-2 ${plan.color} relative`}>
              {plan.current && (
                <span className="absolute -top-3 left-5 badge bg-blue-600 text-white text-xs px-2.5 py-1">Current Plan</span>
              )}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className={`badge ${plan.badge} mb-2`}>{plan.name}</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-sm text-gray-400">{plan.period}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{plan.tokens}</p>
                </div>
              </div>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                    <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                disabled={plan.current}
                className={`w-full justify-center text-sm py-2.5 font-semibold rounded-lg transition-all duration-150 flex items-center gap-2 ${
                  plan.current
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'btn-primary'
                }`}
              >
                {plan.current ? (
                  <><CheckCircle2 size={14} /> {plan.cta}</>
                ) : (
                  <><Zap size={14} /> {plan.cta}</>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Billing history */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-900">Billing History</p>
          <button className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
            <Download size={13} />
            Download All
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {[
            { date: 'Apr 1, 2026', amount: '$299.00', status: 'Paid', invoice: 'INV-2026-04' },
            { date: 'Mar 1, 2026', amount: '$299.00', status: 'Paid', invoice: 'INV-2026-03' },
            { date: 'Feb 1, 2026', amount: '$299.00', status: 'Paid', invoice: 'INV-2026-02' },
          ].map((b) => (
            <div key={b.invoice} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle2 size={14} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{b.invoice}</p>
                  <p className="text-xs text-gray-400">{b.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="badge badge-green">{b.status}</span>
                <span className="text-sm font-bold text-gray-900">{b.amount}</span>
                <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                  <Download size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AgencyPortal() {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const pageTitles: Record<string, string> = {
    overview: 'Agency Overview',
    wallet: 'Token Wallet',
    clients: 'Client Applications',
    history: 'Usage History',
    subscription: 'Subscription',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-100 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:flex`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-gray-100">
          <AppLogo size={32} />
          <span className="font-bold text-gray-900 tracking-tight">VisaPilot</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-50 text-violet-600 border border-violet-100">Agency</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map((item) => {
            const NavIcon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <NavIcon size={17} className={isActive ? 'text-blue-600' : 'text-gray-400'} />
                {item.label}
              </button>
            );
          })}

          <div className="pt-4">
            <Link
              href="/visa-application-wizard"
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150"
            >
              <Plus size={17} />
              New Client Application
            </Link>
          </div>
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
              TP
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">TravelPro Agency</p>
              <p className="text-xs text-gray-400 truncate">Basic Plan</p>
            </div>
            <Settings size={15} className="text-gray-400 flex-shrink-0" />
          </div>
          <Link
            href="/sign-up-login-screen"
            className="w-full flex items-center gap-3 px-3.5 py-2 mt-1 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all duration-150"
          >
            <LogOut size={15} />
            Sign out
          </Link>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-5 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900">{pageTitles[activeTab]}</h1>
              <p className="text-xs text-gray-400 hidden sm:block">TravelPro Agency · B2B Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Bell size={17} className="text-gray-500" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
              TP
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-5 lg:px-8 py-7 overflow-auto">
          {activeTab === 'overview' && <OverviewTab onTabChange={setActiveTab} />}
          {activeTab === 'wallet' && <WalletTab />}
          {activeTab === 'clients' && <ClientsTab />}
          {activeTab === 'history' && <HistoryTab />}
          {activeTab === 'subscription' && <SubscriptionTab />}
        </main>
      </div>
    </div>
  );
}
